import React from 'react';
import { motion } from 'framer-motion';
import { Wallet, Plus, TrendingUp, DollarSign } from 'lucide-react';
import { formatPrice } from '@/config/pricing';
import { Button } from '@/components/ui/button';

interface WalletBalanceProps {
  balance: number;
  onTopUp: () => void;
  className?: string;
}

export const WalletBalance: React.FC<WalletBalanceProps> = ({
  balance,
  onTopUp,
  className = '',
}) => {
  const isLowBalance = balance < 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-xl bg-white border-2 border-slate-200 p-6 shadow-xl ${className}`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-64 h-64 bg-collectability-blue rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-collectability-blue rounded-full translate-x-1/3 translate-y-1/3" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-5 h-5 text-slate-600" />
              <span className="text-sm font-medium text-slate-600">Wallet Balance</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold tracking-tight text-slate-900">
                {formatPrice(balance)}
              </span>
              {isLowBalance && balance > 0 && (
                <span className="text-xs font-semibold bg-amber-500 text-white px-2 py-1 rounded-full">
                  Low
                </span>
              )}
              {balance === 0 && (
                <span className="text-xs font-semibold bg-red-500 text-white px-2 py-1 rounded-full">
                  Empty
                </span>
              )}
            </div>
          </div>

          <Button
            onClick={onTopUp}
            className="bg-collectability-blue text-white hover:bg-collectability-blue-dark shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Funds
          </Button>
        </div>

        {/* Usage Info */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-slate-600" />
            <div>
              <p className="text-xs text-slate-600">Search from</p>
              <p className="text-sm font-semibold text-slate-900">$0.05 each</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-slate-600" />
            <div>
              <p className="text-xs text-slate-600">Savings</p>
              <p className="text-sm font-semibold text-slate-900">Up to 94%</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
